import { SITE_PUBLIC_NAME, COMPANY } from '../data/legalCompanyInfo';
import { SITE_ORIGIN } from './seoLocale';
import { stripSchemaContext } from './structuredDataGraph';

export const ORGANIZATION_ID = `${SITE_ORIGIN}/#organization`;
export const WEBSITE_ID = `${SITE_ORIGIN}/#website`;
export const SOFTWARE_APPLICATION_ID = `${SITE_ORIGIN}/#software-application`;
export const PRODUCT_ID = `${SITE_ORIGIN}/#product`;
export const PRODUCT_IMAGE_URL = `${SITE_ORIGIN}/og-image.png`;
export const OFFER_TRIAL_ID = `${SITE_ORIGIN}/#offer-trial`;

/** Aligned with backend stripeService.js pack prices (EUR). */
export const PRICING_PACKS = [
  { sku: 'pack-10', stripeKey: 'pack_10', credits: 10, price: '15' },
  { sku: 'pack-50', stripeKey: 'pack_50', credits: 50, price: '65' },
  { sku: 'pack-100', stripeKey: 'pack_100', credits: 100, price: '110' },
];

export const DEFAULT_ORG_DESCRIPTION =
  'Dépannage vidéo client instantané, navigateur seul, packs de crédits, infrastructure conforme RGPD.';

const SAME_AS = ['https://www.linkedin.com/company/leagora'];

/** Aligned with CGV section 9 (14-day retraction for unused credits). */
export const MERCHANT_RETURN_POLICY = {
  '@type': 'MerchantReturnPolicy',
  applicableCountry: 'FR',
  returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
  merchantReturnDays: 14,
  returnFees: 'https://schema.org/FreeReturn',
  refundType: 'https://schema.org/FullRefund',
  merchantReturnLink: `${SITE_ORIGIN}/legal/cgv/`,
};

/** Instant digital delivery of session credits (no physical shipping). */
export const OFFER_SHIPPING_DETAILS = {
  '@type': 'OfferShippingDetails',
  shippingRate: { '@type': 'MonetaryAmount', value: '0', currency: 'EUR' },
  shippingDestination: { '@type': 'DefinedRegion', addressCountry: 'FR' },
  deliveryTime: {
    '@type': 'ShippingDeliveryTime',
    handlingTime: { '@type': 'QuantitativeValue', minValue: 0, maxValue: 0, unitCode: 'DAY' },
    transitTime: { '@type': 'QuantitativeValue', minValue: 0, maxValue: 0, unitCode: 'DAY' },
  },
};

function extractNodes(input) {
  if (Array.isArray(input)) {
    return input.map(stripSchemaContext);
  }

  if (input && Array.isArray(input['@graph'])) {
    return input['@graph'].map(stripSchemaContext);
  }

  if (input && input['@type']) {
    return [stripSchemaContext(input)];
  }

  return [];
}

function hasSchemaType(nodes, type) {
  return nodes.some((node) => {
    const nodeType = node['@type'];
    return nodeType === type || (Array.isArray(nodeType) && nodeType.includes(type));
  });
}

export function buildOrganizationNode({ description = DEFAULT_ORG_DESCRIPTION } = {}) {
  return {
    '@type': 'Organization',
    '@id': ORGANIZATION_ID,
    name: SITE_PUBLIC_NAME,
    legalName: COMPANY.legalName,
    url: SITE_ORIGIN,
    logo: {
      '@type': 'ImageObject',
      url: `${SITE_ORIGIN}/og-image.png`,
    },
    description,
    sameAs: SAME_AS,
  };
}

export function buildWebSiteNode() {
  return {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    name: SITE_PUBLIC_NAME,
    alternateName: [COMPANY.siteName],
    url: SITE_ORIGIN,
    description: DEFAULT_ORG_DESCRIPTION,
    publisher: { '@id': ORGANIZATION_ID },
    inLanguage: ['fr-FR', 'en-GB'],
  };
}

export function buildBreadcrumbListNode({ items, pageId }) {
  return {
    '@type': 'BreadcrumbList',
    ...(pageId ? { '@id': pageId } : {}),
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      ...(item.url ? { item: item.url } : {}),
    })),
  };
}

export function buildOfferNode({
  id = OFFER_TRIAL_ID,
  price = '0',
  priceCurrency = 'EUR',
  url = `${SITE_ORIGIN}/register`,
  description,
  includeId = false,
} = {}) {
  return {
    '@type': 'Offer',
    ...(includeId ? { '@id': id } : {}),
    price,
    priceCurrency,
    availability: 'https://schema.org/InStock',
    itemCondition: 'https://schema.org/NewCondition',
    url,
    hasMerchantReturnPolicy: MERCHANT_RETURN_POLICY,
    shippingDetails: OFFER_SHIPPING_DETAILS,
    ...(description ? { description } : {}),
  };
}

export function buildProductNode({ description, offer, reviews = [] } = {}) {
  return {
    '@type': 'Product',
    '@id': PRODUCT_ID,
    name: 'Assistance Video',
    description: description || DEFAULT_ORG_DESCRIPTION,
    image: PRODUCT_IMAGE_URL,
    brand: {
      '@type': 'Brand',
      name: 'Assistance Video',
    },
    offers: offer,
    ...(reviews.length ? { review: reviews } : {}),
  };
}

export function buildPricingPackProductNode({ pack, canonicalUrl }) {
  return {
    '@type': 'Product',
    '@id': `${canonicalUrl}#${pack.sku}`,
    name: pack.name,
    description: pack.description,
    image: PRODUCT_IMAGE_URL,
    sku: pack.sku,
    brand: {
      '@type': 'Brand',
      name: 'Assistance Video',
    },
    offers: buildOfferNode({
      price: pack.priceSchema,
      url: `${canonicalUrl}#${pack.sku}`,
      description: pack.per,
    }),
  };
}

export function buildSoftwareApplicationNode({ description, offer } = {}) {
  return {
    '@type': 'SoftwareApplication',
    '@id': SOFTWARE_APPLICATION_ID,
    name: 'Assistance Video',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    url: SITE_ORIGIN,
    description: description || DEFAULT_ORG_DESCRIPTION,
    offers: offer,
  };
}

export function buildFaqPageNode({ items, pageId }) {
  return {
    '@type': 'FAQPage',
    ...(pageId ? { '@id': pageId } : {}),
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.q || item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a || item.answer,
      },
    })),
  };
}

export function buildArticleNode({
  canonicalUrl,
  headline,
  description,
  datePublished = '2026-05-24',
  dateModified = '2026-05-24',
} = {}) {
  return {
    '@type': 'Article',
    '@id': `${canonicalUrl}#article`,
    headline,
    description,
    datePublished,
    dateModified,
    author: { '@type': 'Organization', name: SITE_PUBLIC_NAME },
    publisher: {
      '@type': 'Organization',
      name: 'Assistance Video',
      logo: { '@type': 'ImageObject', url: `${SITE_ORIGIN}/favicon-48x48.png` },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
  };
}

export function buildHowToNode({ howTo, pageId }) {
  return {
    '@type': 'HowTo',
    '@id': pageId,
    name: howTo.name,
    ...(howTo.description ? { description: howTo.description } : {}),
    step: howTo.steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.text,
    })),
  };
}

/** Graphe JSON-LD pour pages de contenu (articles, piliers, guides). */
export function buildContentPageStructuredGraph({
  content,
  canonicalUrl,
  homeLabel = 'Accueil',
  includeSoftwareOffer = false,
  locale = 'fr',
} = {}) {
  const faqItems = content.faq || [];
  const absoluteContentUrl = (href) => {
    if (!href) {
      return canonicalUrl;
    }
    if (href === '/') {
      return locale === 'en' ? `${SITE_ORIGIN}/en` : `${SITE_ORIGIN}/`;
    }
    const normalized = href.startsWith('/') ? href : `/${href}`;
    return locale === 'en' ? `${SITE_ORIGIN}/en${normalized}` : `${SITE_ORIGIN}${normalized}`;
  };

  const graph = [
    buildOrganizationNode({ description: content.description }),
    buildWebSiteNode(),
    buildArticleNode({
      canonicalUrl,
      headline: content.h1,
      description: content.description,
      datePublished: content.datePublished || '2026-05-24',
      dateModified: content.dateModified || '2026-05-24',
    }),
  ];

  if (faqItems.length) {
    graph.push(buildFaqPageNode({ items: faqItems, pageId: `${canonicalUrl}#faq` }));
  }

  if (content.howTo) {
    graph.push(buildHowToNode({ howTo: content.howTo, pageId: `${canonicalUrl}#howto` }));
  }

  if (includeSoftwareOffer) {
    graph.push(
      buildSoftwareApplicationNode({
        description: content.productDescription || content.description,
        offer: buildOfferNode({
          price: '15',
          url: `${SITE_ORIGIN}/register`,
        }),
      })
    );
  }

  graph.push(
    buildBreadcrumbListNode({
      pageId: `${canonicalUrl}#breadcrumb`,
      items: (content.breadcrumbs?.length
        ? content.breadcrumbs
        : [{ label: homeLabel, href: '/' }, { label: content.h1 }]
      ).map((crumb) => ({
        name: crumb.label,
        url: absoluteContentUrl(crumb.href),
      })),
    })
  );

  return graph;
}

/** Graphe minimal pour pages légales (noindex). */
export function buildLegalPageStructuredGraph({
  canonicalUrl,
  pageTitle,
  homeLabel = 'Accueil',
  homeUrl,
} = {}) {
  return [
    buildOrganizationNode(),
    buildWebSiteNode(),
    buildBreadcrumbListNode({
      pageId: `${canonicalUrl}#breadcrumb`,
      items: [
        { name: homeLabel, url: homeUrl },
        { name: pageTitle, url: canonicalUrl },
      ],
    }),
  ];
}

/** Graph JSON-LD pour la page tarifs (fiches marchand). */
export function buildPricingPageStructuredGraph({
  packs = [],
  faqItems = [],
  canonicalUrl,
  homeLabel = 'Accueil',
  pricingLabel = 'Tarifs',
  orgDescription = DEFAULT_ORG_DESCRIPTION,
  locale = 'fr',
} = {}) {
  const homeUrl = locale === 'en' ? `${SITE_ORIGIN}/en` : `${SITE_ORIGIN}/`;

  const graph = [
    buildOrganizationNode({ description: orgDescription }),
    buildWebSiteNode(),
    buildBreadcrumbListNode({
      pageId: `${canonicalUrl}#breadcrumb`,
      items: [
        { name: homeLabel, url: homeUrl },
        { name: pricingLabel, url: canonicalUrl },
      ],
    }),
    ...packs.map((pack) => buildPricingPackProductNode({ pack, canonicalUrl })),
  ];

  if (faqItems.length) {
    graph.push(buildFaqPageNode({ items: faqItems, pageId: `${canonicalUrl}#faq` }));
  }

  return graph;
}

/** Graph JSON-LD complet pour la page d'accueil (sans Product — voir /tarifs/). */
export function buildHomepageStructuredGraph({
  faqItems = [],
  orgDescription = DEFAULT_ORG_DESCRIPTION,
  canonicalUrl = SITE_ORIGIN,
  homeLabel = 'Accueil',
} = {}) {
  const breadcrumbId = `${canonicalUrl}#breadcrumb`;
  const faqId = `${canonicalUrl}#faq`;

  return [
    buildOrganizationNode({ description: orgDescription }),
    buildWebSiteNode(),
    buildBreadcrumbListNode({
      pageId: breadcrumbId,
      items: [{ name: homeLabel, url: canonicalUrl }],
    }),
    ...(faqItems.length ? [buildFaqPageNode({ items: faqItems, pageId: faqId })] : []),
  ];
}

/** Ajoute Organization et WebSite à tout graphe de page publique si absents. */
export function withSiteStructuredData(input, { orgDescription } = {}) {
  const nodes = extractNodes(input);
  const siteNodes = [];

  if (!hasSchemaType(nodes, 'Organization')) {
    siteNodes.push(buildOrganizationNode({ description: orgDescription }));
  }

  if (!hasSchemaType(nodes, 'WebSite')) {
    siteNodes.push(buildWebSiteNode());
  }

  if (!siteNodes.length) {
    return input;
  }

  return [...siteNodes, ...nodes];
}
